import classNames from 'classnames';
import React, { Component } from 'react';

import { getOpenerContent, isItemSelected } from '@commons/Dropdown/Simple/common';
import type { DropdownItem } from '@commons/Dropdown/Simple/types';
import styles from '@commons/Dropdown/dropdown.scss';
import Icon, { ICON_TYPES } from '@commons/Icon';
import MobileModal from '@commons/Select/mobile/mobile-modal';

import layoutStyles from '@css/layout.scss';

import {
  isDropdownAccessibilityEnabled,
  isIosDropdownOpeningWhenKeyboardShownFixEnabled
} from '@services/featureFlags';

import { isSafariMobile } from '@utils/browserAgent';
import { afterSafariKeyboardCloses } from '@utils/iosDisplay';
import stripHtml from '@utils/stripHtml';

import MobileDropdownItem from './MobileItem';

interface MobileDropdownProps {
  label?: string;
  selected?: unknown;
  dropdownList?: DropdownItem[];
  disabled?: boolean;
  selectItem: (item: DropdownItem) => void;
  isMultiValued?: boolean;
  isRTL?: boolean;
}

interface MobileDropdownState {
  listInvisible: boolean;
  focusedIndex: number;
}

class MobileDropdown extends Component<MobileDropdownProps, MobileDropdownState> {
  static defaultProps = {
    label: '',
    selected: null,
    dropdownList: [],
    disabled: false,
    isMultiValued: false
  };

  state = { listInvisible: true, focusedIndex: 0 };

  listRef = React.createRef<HTMLUListElement>();

  triggerRef = React.createRef<HTMLButtonElement | HTMLDivElement>();

  componentDidUpdate(_prevProps: MobileDropdownProps, prevState: MobileDropdownState) {
    const { listInvisible, focusedIndex } = this.state;

    if (!listInvisible && prevState.listInvisible) {
      // Modal just opened: attach keyboard listener and focus the selected/first item
      document.addEventListener('keydown', this.handleDocumentKeyDown);
      requestAnimationFrame(() => this.focusItem(focusedIndex));
    } else if (!listInvisible && prevState.focusedIndex !== focusedIndex) {
      // Arrow key moved focus: update DOM focus
      this.focusItem(focusedIndex);
    }

    if (listInvisible && !prevState.listInvisible) {
      // Modal closed: clean up listener (react-aria restores focus to trigger automatically)
      document.removeEventListener('keydown', this.handleDocumentKeyDown);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleDocumentKeyDown);
  }

  // Focus the item at the given index in the dropdown list
  focusItem = (index: number) => {
    const item = this.listRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]')[index];
    item?.focus();
  };

  handleShowMobile = () => {
    if (this.props.disabled) return;
    if (!isSafariMobile() || !isIosDropdownOpeningWhenKeyboardShownFixEnabled()) {
      return this.openModal();
    }
    return afterSafariKeyboardCloses().then(this.openModal);
  };

  openModal = () => {
    const { selected, dropdownList, isMultiValued } = this.props;
    const list = dropdownList || [];
    let focusedIndex = 0;
    if (isMultiValued && Array.isArray(selected) && selected.length > 0) {
      // Focus the first selected item in the list (for multi-select dropdowns)
      const selectedIds = new Set((selected as DropdownItem[]).map((item) => item.id));
      const idx = list.findIndex((item) => selectedIds.has(item.id));
      if (idx >= 0) focusedIndex = idx;
    } else if (!isMultiValued && selected) {
      const idx = list.findIndex((item) => item.id === (selected as DropdownItem).id);
      if (idx >= 0) focusedIndex = idx;
    }
    this.setState({ listInvisible: false, focusedIndex });
  };

  handleHide = () => {
    this.setState({ listInvisible: true });
  };

  handleFocusedIndex = (index: number) => {
    this.setState({ focusedIndex: index });
  };

  handleDocumentKeyDown = (event: KeyboardEvent) => {
    const list = this.props.dropdownList || [];
    const { selectItem, isMultiValued } = this.props;
    const PAGE_SIZE = 7;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.setState((prev) => ({
          focusedIndex: Math.min(prev.focusedIndex + 1, list.length - 1)
        }));
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.setState((prev) => ({
          focusedIndex: Math.max(prev.focusedIndex - 1, 0)
        }));
        break;
      case 'PageDown':
        event.preventDefault();
        this.setState((prev) => ({
          focusedIndex: Math.min(prev.focusedIndex + PAGE_SIZE, list.length - 1)
        }));
        break;
      case 'PageUp':
        event.preventDefault();
        this.setState((prev) => ({
          focusedIndex: Math.max(prev.focusedIndex - PAGE_SIZE, 0)
        }));
        break;
      case 'Home':
        event.preventDefault();
        this.setState({ focusedIndex: 0 });
        break;
      case 'End':
        event.preventDefault();
        this.setState({ focusedIndex: list.length - 1 });
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (list[this.state.focusedIndex]) {
          selectItem(list[this.state.focusedIndex]);
          if (!isMultiValued) this.handleHide();
        }
        break;
      case 'Escape':
        this.handleHide();
        break;
      case 'Tab':
        event.preventDefault();
        this.handleHide();
        this.triggerRef.current?.focus();
        break;
      default:
        break;
    }
  };

  handleItemClick = () => {
    const { isMultiValued } = this.props;

    if (!isMultiValued) {
      this.handleHide();
    }
  };

  render() {
    const {
      isDropdownLabelInactive,
      label,
      dropdownList,
      title,
      containerStyles,
      selectorStyles,
      selectItem,
      calendarTitleStyles,
      calendarIconStyles,
      ariaRequired,
      ariaDisabled,
      ariaLabel,
      ariaInvalid,
      disabled,
      selected,
      isMultiValued,
      isRTL
    } = this.props;

    const dropdownTitle = stripHtml(title || label);

    const labelClasses = classNames(styles.dropdownLabel, {
      [styles.dropdownLabel_isInactive]: isDropdownLabelInactive
    });

    const selectorClasses = classNames(
      styles.dropdownSelector,
      styles.dropdownSelector_isInactive,
      {
        [styles.dropdownSelector_isDisable]: disabled
      }
    );

    const DropdownOpener = isDropdownAccessibilityEnabled() ? 'button' : 'div';
    const openerAriaAttributes = isDropdownAccessibilityEnabled()
      ? { type: 'button' }
      : {
        role: 'button',
        tabIndex: 0
      };

    return (
      <div className={layoutStyles.mobileScreenOnly}>
        {label && <div className={labelClasses}>{label}</div>}
        <div className={`${styles.dropdownContainer} ${containerStyles}`}>
          <DropdownOpener
            className={classNames(selectorClasses, selectorStyles)}
            ref={this.triggerRef}
            onClick={this.handleShowMobile}
            title={dropdownTitle}
            aria-haspopup="true"
            aria-expanded={!this.state.listInvisible}
            {...openerAriaAttributes}
            {...ariaInvalid}
            {...ariaRequired}
            {...ariaLabel}
            {...ariaDisabled}
          >
            <div className={calendarTitleStyles}>{getOpenerContent(this.props)}</div>
            <Icon containerStyles={calendarIconStyles} type={ICON_TYPES.ARROW_DROPDOWN} />
          </DropdownOpener>
          <MobileModal
            isOpen={!this.state.listInvisible}
            onClose={this.handleHide}
            title={dropdownTitle}
            isRTL={isRTL}
          >
            <ul
              role="menu"
              className={`${styles.dropdownList} ${styles.dropdownList_isActive}`}
              onClick={this.handleItemClick}
              ref={this.listRef}
            >
              {(dropdownList || []).map((item, index) => (
                <MobileDropdownItem
                  key={item.id}
                  index={index}
                  isFocused={!this.state.listInvisible && index === this.state.focusedIndex}
                  selected={isItemSelected({ itemId: item.id, isMultiValued, selected })}
                  selectItem={selectItem}
                  onFocusItem={this.handleFocusedIndex}
                  item={item}
                  isMultiValued={isMultiValued}
                />
              ))}
            </ul>
          </MobileModal>
        </div>
      </div>
    );
  }
}

export default MobileDropdown;
